import { deactivateEmployee } from '@/lib/employees';

/**
 * HW-803: deactivating a manager is refused while their direct reports still
 * point at them. The backend answers with 409 `HAS_DIRECT_REPORTS` and the list
 * of people left unsettled; the UI turns that into a reassignment dialog and
 * retries the same request with an answer for every one of them.
 */

/** A direct report named in the 409 payload. */
export interface UnsettledDirectReport {
  id: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  /** Fallback label: a record can carry blank names, and a blank row is unclickable-by-accident bait. */
  workEmail: string;
  status: string;
  positionTitle: string | null;
}

/** One line of the answer sent back: a new manager, or `null` for nobody. */
export interface ReportReassignment {
  employeeId: string;
  managerId: string | null;
}

/**
 * A choice made in the dialog. `null` is the deliberate "leave them without a
 * manager"; a report missing from the map has not been answered yet — the
 * difference is what keeps the Deactivate button disabled.
 */
export type ReportChoice = string | null;
export type ReportChoices = Record<string, ReportChoice>;

const CONFLICT_CODE = 'HAS_DIRECT_REPORTS';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toReport(value: unknown): UnsettledDirectReport | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    firstName: typeof value.firstName === 'string' ? value.firstName : '',
    lastName: typeof value.lastName === 'string' ? value.lastName : '',
    preferredName: typeof value.preferredName === 'string' ? value.preferredName : null,
    workEmail: typeof value.workEmail === 'string' ? value.workEmail : '',
    status: typeof value.status === 'string' ? value.status : 'active',
    positionTitle: typeof value.positionTitle === 'string' ? value.positionTitle : null,
  };
}

/**
 * Pulls the report list out of a failed deactivation. Returns `null` for every
 * other failure, so callers can fall through to their usual error toast.
 */
export function parseDirectReportsConflict(error: unknown): UnsettledDirectReport[] | null {
  if (!isRecord(error)) return null;
  const response = isRecord(error.response) ? error.response : null;
  if (response?.status !== 409) return null;

  const body = isRecord(response.data) ? response.data : null;
  if (body?.code !== CONFLICT_CODE) return null;

  const rows = Array.isArray(body.directReports) ? body.directReports : [];
  const reports = rows.map(toReport).filter((r): r is UnsettledDirectReport => r !== null);
  return reports.length > 0 ? reports : null;
}

/**
 * Builds the payload for the retry. Every report must carry its own answer —
 * "reassign everyone to X" is an action that writes those answers, not a
 * fallback consulted here, so an answer cannot go missing when the list
 * changes. Returns `null` while any report is unanswered: a partial answer must
 * not be submitted, and the backend would refuse it anyway.
 */
export function buildReportReassignments(
  reports: Array<{ id: string }>,
  choices: ReportChoices
): ReportReassignment[] | null {
  const assignments: ReportReassignment[] = [];

  for (const report of reports) {
    if (!Object.prototype.hasOwnProperty.call(choices, report.id)) return null;

    const managerId = choices[report.id];
    if (managerId === report.id) return null; // nobody manages themselves
    assignments.push({ employeeId: report.id, managerId: managerId ?? null });
  }

  return assignments;
}

/**
 * Deactivates an employee, turning the backend's refusal into data instead of an
 * exception. `{ ok: false }` means the reports have to be settled first — and it
 * is returned on a retry too, so a report added between the two calls reopens
 * the dialog instead of being quietly skipped. Any other failure still throws.
 */
export async function deactivateWithReportHandoff(
  employeeId: string,
  terminationDate?: string,
  assignments?: ReportReassignment[]
): Promise<{ ok: true } | { ok: false; reports: UnsettledDirectReport[] }> {
  try {
    await deactivateEmployee(employeeId, terminationDate, assignments);
    return { ok: true };
  } catch (error) {
    const reports = parseDirectReportsConflict(error);
    if (reports) return { ok: false, reports };
    throw error;
  }
}

/**
 * Who may take over a given report: anybody still working here, except the
 * person being deactivated and the report themselves. Mirrors the checks the
 * backend enforces, so an impossible choice is never offered in the first place.
 */
export function managerOptionsFor<T extends { id: string; status?: string }>(
  reportId: string,
  candidates: T[],
  deactivatedEmployeeId: string
): T[] {
  return candidates.filter(
    (c) => c.id !== reportId && c.id !== deactivatedEmployeeId && c.status !== 'terminated'
  );
}
